import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import 'react-toggle/style.css';
import API from '../../../utils/API';
import FormManager from '../../program/eventDefaults/programFormManager';
import TabContainer from '../../../components/views/TabsContainer';
import InputField from '../../../components/inputs/inputField';
import ToggleField from '../../../components/inputs/toggleField';
import SectionHeader from '../../../components/views/SectionHeader';
import CheckInFormBuilderModal from '../../event/checkin/modals/checkInFormBuilderModal';
import SectionFooter from '../../../components/views/SectionFooter';
import FileChooserPreview from '../../../components/inputs/fileChooserPreview';
import FilesUploaderModal from '../../../components/files/filesUploaderModal';
import baseFieldList from '../../../data/attendeeBaseFieldList';
import SelectField from '../../../components/inputs/selectField';
import CheckinSetup from '../../event/checkin/setup';
import CheckinFormSetup from '../../event/checkin/checkinForm';
import WalkinFormSetup from '../../event/checkin/walkinForm';
import SessionCheckinSetup from '../../event/checkin/sessionCheckin';
import AddSimpleZebraDeviceModal from '../../event/checkin/modals/addSimpleZebraDeviceModal';
import ActionButtonContainer from '../../../components/views/ActionButtonContainer';
import ViewsTable from '../../../components/tables/ViewsTable';
import RenamePrinterModal from '../../event/checkin/modals/renamePrinter';
import DisconnectPrinterModal from '../../event/checkin/modals/disconnectPrinterModal';
import BadgeEditor from '../../../components/badgeEditor';
import LoadingWizard from '../../../components/spinner/wizard';
import ConfigureCheckin from '../../event/checkin/configure';

/**
 * Program-specific Check-In configuration component
 * This component adapts the event check-in functionality for use in programs (event defaults)
 */
class ProgramCheckInSetup extends Component {
    constructor(props) {
        super(props);
        let tabs = [
            { title: 'Setup', id: 'setup' },
            { title: 'Configure', id: 'configure' },
            { title: 'Check In Form', id: 'checkInForm' },
        ];

        const customFieldColumns = {
            friendlyName: 'Name',
            connectedPrinterCount: 'Connected Printers',
        };
        const defaultFieldTypes = {
            friendlyName: 'text',
            connectedPrinterCount: 'number',
        };
        const toggledColumns = {};
        const normalizedColumns = Object.keys(customFieldColumns).map((cfc) => {
            toggledColumns[cfc] = true;
            return {
                label: customFieldColumns[cfc],
                key: cfc,
                value: cfc,
                type: (defaultFieldTypes && defaultFieldTypes[cfc]) || typeof defaultFieldTypes[cfc],
                sortAsc: false,
                sortDesc: false
            };
        });

        this.state = {
            editedFields: props.fields,
            customFields: this.props.fields.map((category) => {
                const title = category.title;
                const headers = category.fields.map((field) => {
                    const name = field.fieldName;
                    return {
                        _id: field._id,
                        fieldName: field.fieldName,
                        fieldType: field.fieldType,
                        category: title,
                        categoryId: category._id,
                        required: false,
                        disabled: false,
                        value: name,
                        label: name,
                        propertyType: 'attendee'
                    };
                });
                return {
                    _id: category._id,
                    title: `${title}`,
                    fields: headers,
                    label: title,
                    value: category._id,
                    options: headers
                };
            }),
            checkInAppConfiguration: {
                enableQRScanner: false,
                enableWalkInCheckIn: false,
                enableSessionCheckIn: false,
                pin: '',
                checkInForm: null,
                walkInForm: null,
                sessionCheckInForm: null,
                useCheckinFormForWalkIn: false,
                useCheckinFormForSession: false,
                showProfilePicture: false,
                showQRCode: false,
                enableConsentScreen: false,
                consentScreen: '',
                backgroundBanner: '',
                name: '',
                shortName: '',
                description: '',
                backgroundColor: '',
                themeColor: '',
                icons: {
                    small: '',
                    medium: '',
                    large: ''
                },
                badgeConfig: {
                    badgeWidth: 88,
                    badgeHeight: 140,
                    name: { x: 6.7, y: 21.2, fontSize: 25 },
                    qrCode: { x: 6.7, y: 96.1, size: 25 },
                    city: { x: 6.7, y: 42.4, fontSize: 12 },
                    backgroundImage: 'https://assets-simpleevents.s3.amazonaws.com/betaorg/SE_66c8ca9f1913e99e9baebe93/assets/BADGETEMPLATE%20in%20MM.png',
                    includeBackground: false
                }
            },
            baseConfiguration: null,
            uploadingCheckinBanner: false,
            tabs,
            checkinFormEditorModalOpen: false,
            walkInFormEditorModalOpen: false,
            sessionCheckInFormEditorModalOpen: false,
            editingFormBuilder: false,
            editedForm: null,
            form: null,
            editCount: 0,
            error: null,
            editing: false,
            linkCopied: false,
            guideCopied: false,
            toggleAddSimpleZebraModal: false,
            normalizedColumns: normalizedColumns,
            toggledColumns: toggledColumns,
            toggleRenamePrinterModal: false,
            toggleDisconnectPrinterModal: false,
            selectedRow: null,
            toggleDisconnectPrinterModal: false,
            badgeEditorModalOpen: false,
            updatedAt: '',
            loading: true
        };

        // Initialize form manager
        this.formManager = new FormManager(this);
    }

    componentDidMount() {
        const { orgId, programId } = this.props;

        // Use program-specific API endpoint
        API().get(`Organizations/${orgId}/programs/${programId}/checkIn`).then((res) => {
            if (res.data) {
                const { walkInForm, checkInForm, sessionCheckInForm, enableSessionCheckIn, enableWalkInCheckIn, enableBadgePrinting } = res.data;

                let tabs = [
                    { title: 'Setup', id: 'setup' },
                    { title: 'Configure', id: 'configure' },
                    { title: 'Check-In Form', id: 'checkInForm' }
                ];
                if (enableWalkInCheckIn) {
                    tabs.push({ title: 'Walk-In Registration Form', id: 'walkInForm' });
                }
                if (enableSessionCheckIn) {
                    tabs.push({ title: 'Session Check-In Form', id: 'sessionCheckIn' });
                }

                try {
                    // Create deep copies of forms to avoid reference issues
                    const formCopies = {
                        checkInForm: checkInForm ? JSON.parse(JSON.stringify(checkInForm)) : null,
                        walkInForm: walkInForm ? JSON.parse(JSON.stringify(walkInForm)) : null,
                        sessionCheckInForm: sessionCheckInForm ? JSON.parse(JSON.stringify(sessionCheckInForm)) : null
                    };

                    // Validate and initialize form structures
                    Object.keys(formCopies).forEach(key => {
                        if (formCopies[key] && !formCopies[key]?.pages?.[0]?.sections) {
                            formCopies[key].pages = [{
                                title: 'Check In',
                                subtitle: '',
                                sections: [{
                                    _id: `section_${Date.now()}`,
                                    title: '',
                                    fields: [],
                                    buttons: []
                                }]
                            }];
                        }
                    });

                    // Initialize configuration with validated forms
                    const updatedConfig = {
                        ...res.data,
                        ...formCopies
                    };
                    updatedConfig.icons = updatedConfig.icons || {};
                    updatedConfig.splashScreens = updatedConfig.splashScreens || {};

                    this.setState({
                        checkInAppConfiguration: updatedConfig,
                        baseConfiguration: JSON.parse(JSON.stringify(updatedConfig)),
                        loading: false,
                        selectedSection: 'setup',
                        tabs,
                        editCount: 0,
                        editedForm: null,
                        form: null,
                        editing: false,
                        editingFormBuilder: false,
                        error: null
                    });
                } catch (error) {
                    console.error('Failed to initialize forms:', error);
                    this.setState({
                        error: 'Failed to initialize forms. Please refresh the page.',
                        loading: false
                    });
                }
            }
        });
    }

    // Save check-in app configuration using program-specific endpoint
    saveCheckInAppConfiguration = async () => {
        const { orgId, programId } = this.props;
        const { checkInAppConfiguration } = this.state;

        let error = null;

        // Always validate admin pin
        if (!checkInAppConfiguration.pin || checkInAppConfiguration.pin.toString().length !== 4) {
            error = 'Please enter a 4 digit admin pin';
        }

        // Validate session pin when session check-in is enabled
        if (checkInAppConfiguration.enableSessionCheckIn && (!checkInAppConfiguration.sessionPin || checkInAppConfiguration.sessionPin.toString().length !== 4)) {
            error = 'Please enter a 4 digit session check-in pin';
        }

        if (checkInAppConfiguration.enabled) {
            if (!checkInAppConfiguration.checkInForm) {
                error = 'Check-in form is required or app must be disabled';
            }
            if (checkInAppConfiguration.enableWalkInCheckIn && !checkInAppConfiguration.useCheckinFormForWalkIn && !checkInAppConfiguration.walkInForm) {
                error = 'Walk-in form is required if not using check-in form';
            }
            if (checkInAppConfiguration.enableSessionCheckIn && !checkInAppConfiguration.useCheckinFormForSession && !checkInAppConfiguration.sessionCheckInForm) {
                error = 'Session check-in form is required if not using check-in form';
            }
        }

        if (error) {
            this.setState({ error });
            return;
        }

        try {
            // Create payload with form IDs
            const payload = { ...checkInAppConfiguration };

            // Clean up form references
            /*  ['sessionCheckInForm', 'walkInForm', 'checkInForm'].forEach(formKey => {
                 if (!payload[formKey]) {
                     delete payload[formKey];
                 } else {
                     payload[formKey] = payload[formKey]._id;
                 }
             }); */

            // Use program-specific API endpoint
            const response = await API().patch(
                `Organizations/${orgId}/programs/${programId}/checkIn`,
                payload
            );
            if (response.data) {
                this.setState({
                    checkInAppConfiguration: response.data,
                    baseConfiguration: JSON.parse(JSON.stringify(response.data)),
                    editing: false
                });
            }
        } catch (error) {
            console.error('Failed to save check-in configuration:', error);
            this.setState({
                error: 'Failed to save configuration. Please try again.'
            });
        }
    }

    cancelEdit = () => {
        const { baseConfiguration } = this.state;
        let tabs = [
            { title: 'Setup', id: 'setup' },
            { title: 'Configure', id: 'configure' },
            { title: 'Check-In Form', id: 'checkInForm' }
        ];
        if (baseConfiguration.enableWalkInCheckIn) {
            tabs.push({ title: 'Walk-In Registration Form', id: 'walkInForm' });
        }
        if (baseConfiguration.enableSessionCheckIn) {
            tabs.push({ title: 'Session Check-In Form', id: 'sessionCheckIn' });
        }

        this.setState({
            editing: false,
            checkInAppConfiguration: JSON.parse(JSON.stringify(baseConfiguration)),
            error: null,
            tabs,
        });
    }

    // Handle configuration changes with validation and state updates
    handleChange = async (changeObj, hiddenEdit) => {
        this.setState(prevState => {
            const updatedConfig = {
                ...prevState.checkInAppConfiguration,
                ...changeObj
            };

            // Update tabs if enabling/disabling walk-in or session check-in
            let updatedTabs = [...prevState.tabs];
            if ('enableWalkInCheckIn' in changeObj || 'enableSessionCheckIn' in changeObj || 'enableBadgePrinting' in changeObj) {
                // Always include Setup and Check In Form
                updatedTabs = [
                    { title: 'Setup', id: 'setup' },
                    { title: 'Configure', id: 'configure' },
                    { title: 'Check In Form', id: 'checkInForm' }
                ];

                // Add conditional tabs
                if (updatedConfig.enableWalkInCheckIn) {
                    updatedTabs.push({ title: 'Walk-In Registration Form', id: 'walkInForm' });
                }
                if (updatedConfig.enableSessionCheckIn) {
                    updatedTabs.push({ title: 'Session Check-In Form', id: 'sessionCheckIn' });
                }
            }

            // Handle pin validation
            if ('pin' in changeObj) {
                const pin = changeObj.pin.toString();
                if (pin.length > 4) {
                    return null; // Don't update state if pin is too long
                }
            }

            return {
                checkInAppConfiguration: updatedConfig,
                tabs: updatedTabs,
                editing: !hiddenEdit,
                error: null // Clear any previous errors
            };
        });
    }

    handleCopyGuide = () => {
        const guideContent = `
		For iOS Devices:
		1. Open the event link in Safari.
		2. Tap the Share button (square with an arrow).
		3. Scroll and select "Add to Home Screen".
		4. Name the app (if desired).
		5. Tap "Add".
		6. The app icon will now appear on your home screen.

		Troubleshooting:
		• Ensure you're using Safari.
		• Check that your iOS is up to date.
		• Close and reopen Safari if needed.
		• Verify you have sufficient device storage.

		For Android Devices:
		1. Open the event link in your mobile browser.
		2. Tap the "Add to Home Screen" button in the browser menu.
		3. Tap "Add" or "Install" in the pop-up window.
		4. The app icon will now appear on your home screen.

		Troubleshooting:
		• If no prompt appears, look for the browser's menu (three dots).
		• Select "Add to Home Screen" from the menu.
		• Ensure you have enough storage space.
		• Restart your browser if installation fails.
        `;

        navigator.clipboard
            .writeText(guideContent)
            .then(() => {
                this.setState({ guideCopied: true });
                setTimeout(() => {
                    this.setState({ guideCopied: false });
                }, 2000);
            })
            .catch((err) => console.error("Failed to copy guide:", err));
    };

    handleCopyEventLink = () => {
        // For programs, the link structure might be different
        const link = this.props.org.hostname
            ? `https://${this.props.org.hostname}/program/${this.props.program.nameSlug}/CheckinApp`
            : `https://${this.props.org.identifier}.simple.events/program/${this.props.program.nameSlug}/CheckinApp`;

        navigator.clipboard
            .writeText(link)
            .then(() => {
                this.setState({ linkCopied: true });
                setTimeout(() => {
                    this.setState({ linkCopied: false });
                }, 2000);
            })
            .catch((err) => console.error("Failed to copy link:", err));
    };

    updateCustomFields = (CustomFieldData) => {
        const updatedCustomFields = [...this.state.customFields];

        // Find if category already exists
        const existingCategoryIndex = updatedCustomFields.findIndex(
            category => category.title === CustomFieldData.title
        );

        if (existingCategoryIndex > -1) {
            // Add new field to existing category
            const newField = {
                _id: CustomFieldData.fields[CustomFieldData.fields.length - 1]._id,
                fieldName: CustomFieldData.fields[CustomFieldData.fields.length - 1].fieldName,
                fieldType: CustomFieldData.fields[CustomFieldData.fields.length - 1].fieldType,
                required: false,
                disabled: false,
                propertyType: 'attendee'
            };
            updatedCustomFields[existingCategoryIndex].fields.push(newField);
        } else {
            const newCategory = {
                _id: CustomFieldData.fields[CustomFieldData.fields.length - 1]._id,
                title: CustomFieldData.title,
                fields: CustomFieldData.fields.map((field) => ({
                    _id: field._id,
                    fieldName: field.fieldName,
                    fieldType: field.fieldType,
                    required: false,
                    disabled: false,
                    propertyType: 'attendee'
                }))
            };
            updatedCustomFields.push(newCategory);
        }

        this.setState({ customFields: updatedCustomFields });
    };

    toggleAddSimpleZebraModal = () => {
        this.setState({
            toggleAddSimpleZebraModal: !this.state.toggleAddSimpleZebraModal
        });
    };

    updatedActiveSimpleZebraDevices = (activeSimpleZebraDevices) => {
        this.setState({
            checkInAppConfiguration: {
                ...this.state.checkInAppConfiguration,
                zebraPrinters: activeSimpleZebraDevices
            }
        });
    };

    toggleRenamePrinterModal = (id) => {
        const { toggleRenamePrinterModal } = this.state;
        this.setState({ toggleRenamePrinterModal: !toggleRenamePrinterModal, selectedRow: id });
    };

    toggleDisconnectPrinterModal = (id) => {
        const { toggleDisconnectPrinterModal } = this.state;
        this.setState({ toggleDisconnectPrinterModal: !toggleDisconnectPrinterModal, selectedRow: id });
    };

    updateSelected = (index) => {
        const { tabs } = this.state;
        this.setState({ selectedSection: tabs[index].id });
    };

    renderSection = () => {
        const { selectedSection, checkInAppConfiguration } = this.state;
        const { orgId, programId, program } = this.props;

        // Use program event defaults as event data
        const eventData = program.eventDefaults.details;

        switch (selectedSection) {
            case 'setup':
                return <CheckinSetup
                    checkInAppConfiguration={checkInAppConfiguration}
                    handleChange={this.handleChange}
                    orgId={orgId}
                    programId={programId}
                    uploadingCheckinBanner={this.state.uploadingCheckinBanner}
                    updateUploadingCheckinBanner={(val) => {
                        this.setState({ uploadingCheckinBanner: val });
                    }}
                    event={eventData}
                />
            case 'configure':
                return <ConfigureCheckin
                    checkInAppConfiguration={checkInAppConfiguration}
                    handleChange={this.handleChange}
                    orgId={orgId}
                    programId={programId}
                    uploadingCheckinBanner={this.state.uploadingCheckinBanner}
                    updateUploadingCheckinBanner={(val) => {
                        this.setState({ uploadingCheckinBanner: val });
                    }}
                    event={eventData}
                />
            case 'checkInForm':
                let checkInFormId = !checkInAppConfiguration.checkInForm ? null : checkInAppConfiguration.checkInForm._id;
                return <CheckinFormSetup
                    formManager={this.formManager}
                    checkInAppConfiguration={checkInAppConfiguration}
                    customFields={this.state.customFields}
                    orgId={orgId}
                    programId={programId}
                    checkInFormId={checkInFormId}
                    editCount={this.state.editCount}
                    account={this.props.account}
                    handleChange={this.handleChange}
                    updateCustomFields={this.updateCustomFields}
                />
            case 'walkInForm':
                let walkInFormId = !checkInAppConfiguration.walkInForm ? null : checkInAppConfiguration.walkInForm._id;
                return <WalkinFormSetup
                    formManager={this.formManager}
                    checkInAppConfiguration={checkInAppConfiguration}
                    customFields={this.state.customFields}
                    orgId={orgId}
                    programId={programId}
                    walkInFormId={walkInFormId}
                    editCount={this.state.editCount}
                    account={this.props.account}
                    handleChange={this.handleChange}
                    updateCustomFields={this.updateCustomFields}
                />
            case 'sessionCheckIn':
                let sessionCheckInFormId = !checkInAppConfiguration.sessionCheckInForm ? null : checkInAppConfiguration.sessionCheckInForm._id;
                return <SessionCheckinSetup
                    formManager={this.formManager}
                    checkInAppConfiguration={checkInAppConfiguration}
                    customFields={this.state.customFields}
                    orgId={orgId}
                    programId={programId}
                    sessionCheckInFormId={sessionCheckInFormId}
                    editCount={this.state.editCount}
                    account={this.props.account}
                    handleChange={this.handleChange}
                    updateCustomFields={this.updateCustomFields}
                />
            default:
                return null;
        }
    }

    render() {
        const {
            loading,
            tabs,
            selectedSection,
            editing,
            error,
            checkInAppConfiguration,
            checkinFormEditorModalOpen,
            walkInFormEditorModalOpen,
            sessionCheckInFormEditorModalOpen,
            editingFormBuilder
        } = this.state;

        if (loading) {
            return <LoadingWizard loading={loading} text="Loading Check-In Configuration" />;
        }

        return (
            <div className="sectionContainer">
                <SectionHeader
                    title="Check-In App Template"
                    breadcrumbs={[
                        {
                            name: this.props.program.name
                        }
                    ]}
                >   <TabContainer
                        tabs={tabs}
                        updateSelected={this.updateSelected}
                        selectedIndex={tabs.findIndex((tab) => tab.id === selectedSection)}
                    />
                </SectionHeader>


                {this.renderSection()}
                {this.state.error && (
                    <div className="error-message mb-20" style={{ color: 'red', padding: '10px', textAlign: 'center' }}>
                        {this.state.error}
                    </div>
                )}
                <SectionFooter
                    editing={editing}
                    cancelEdit={this.cancelEdit}
                    saveEdit={this.saveCheckInAppConfiguration}
                    updatedAt={checkInAppConfiguration.updatedAt}
                />

                {checkinFormEditorModalOpen && (
                    <CheckInFormBuilderModal
                        limitPages={true}
                        isOpen={checkinFormEditorModalOpen}
                        editing={editingFormBuilder}
                        toggle={() => this.setState({
                            checkinFormEditorModalOpen: false,
                            editedForm: null,
                            form: null,
                            editingFormBuilder: false,
                            error: null
                        })}
                        form={this.state.form || checkInAppConfiguration.checkInForm}
                        editedForm={this.state.editedForm || checkInAppConfiguration.checkInForm}
                        fields={this.props.fields}
                        baseFieldList={[...baseFieldList]}
                        links={{ get: `` }}
                        updateForm={(form) => this.formManager.updateForm('checkInForm', form)}
                        cancelEdit={this.formManager.cancelEdit}
                        emails={[]}
                        orgId={this.props.orgId}
                        programId={this.props.programId}
                        addNewField={null}
                        saveForm={this.formManager.saveForm}
                        customFieldList={[...this.state.customFields]}
                        groups={[]}
                        updateCustomFields={this.updateCustomFields}
                    />
                )}

                {walkInFormEditorModalOpen && (
                    <CheckInFormBuilderModal
                        limitPages={true}
                        isOpen={walkInFormEditorModalOpen}
                        editing={editingFormBuilder}
                        toggle={() => this.setState({
                            walkInFormEditorModalOpen: false,
                            editedForm: null,
                            form: null,
                            editingFormBuilder: false,
                            error: null
                        })}
                        form={this.state.form || checkInAppConfiguration.walkInForm}
                        editedForm={this.state.editedForm || checkInAppConfiguration.walkInForm}
                        fields={this.props.fields}
                        baseFieldList={[...baseFieldList]}
                        links={{ get: `` }}
                        updateForm={(form) => this.formManager.updateForm('walkInForm', form)}
                        cancelEdit={this.formManager.cancelEdit}
                        emails={[]}
                        orgId={this.props.orgId}
                        programId={this.props.programId}
                        addNewField={null}
                        saveForm={this.formManager.saveForm}
                        customFieldList={[...this.state.customFields]}
                        groups={[]}
                        updateCustomFields={this.updateCustomFields}
                    />
                )}

                {sessionCheckInFormEditorModalOpen && (
                    <CheckInFormBuilderModal
                        limitPages={true}
                        isOpen={sessionCheckInFormEditorModalOpen}
                        editing={editingFormBuilder}
                        toggle={() => this.setState({
                            sessionCheckInFormEditorModalOpen: false,
                            editedForm: null,
                            form: null,
                            editingFormBuilder: false,
                            error: null
                        })}
                        form={this.state.form || checkInAppConfiguration.sessionCheckInForm}
                        editedForm={this.state.editedForm || checkInAppConfiguration.sessionCheckInForm}
                        fields={this.props.fields}
                        baseFieldList={[...baseFieldList]}
                        links={{ get: `` }}
                        updateForm={(form) => this.formManager.updateForm('sessionCheckIn', form, true)}
                        cancelEdit={this.formManager.cancelEdit}
                        emails={[]}
                        orgId={this.props.orgId}
                        programId={this.props.programId}
                        addNewField={null}
                        saveForm={this.formManager.saveForm}
                        customFieldList={[...this.state.customFields]}
                        groups={[]}
                        updateCustomFields={this.updateCustomFields}
                    />
                )}

                {this.state.badgeEditorModalOpen && (
                    <BadgeEditor
                        isOpen={this.state.badgeEditorModalOpen}
                        toggle={() => this.setState({ badgeEditorModalOpen: false })}
                        programId={this.props.programId}
                        orgId={this.props.orgId}
                        initialConfig={checkInAppConfiguration.badgeConfiguration}
                        links={{
                            getAssets: `Organizations/${this.props.orgId}/programs/${this.props.programId}/assets`,
                            addAssets: `Organizations/${this.props.orgId}/programs/${this.props.programId}/assets`,
                        }}
                        handleChange={this.handleChange}
                        saveCheckInAppConfiguration={this.saveCheckInAppConfiguration}
                    />
                )}
            </div>
        );
    }
}

export default withRouter(ProgramCheckInSetup);
